import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { apis } from '../config/api';

export interface PortalStudent {
  id: string;
  firstName: string;
  secondName?: string | null;
  thirdName?: string | null;
  phone?: string | null;
  phoneCountryIso?: string | null;
  gender?: number | null;
  status?: number | null;
  relationship?: number | null;
  isOwner?: boolean;
  schoolId: string;
  userId: string;
  createdAt: string;
  user?: {
    id: string;
    email: string;
    firstName: string;
    secondName?: string | null;
    thirdName?: string | null;
    phone?: string | null;
    phoneCountryIso?: string | null;
    nationalityIso?: string | null;
    address?: string | null;
    image?: string | null;
  };
  school?: {
    id: string;
    name: string;
    logo?: string | null;
    currency?: string;
    timezone?: string;
    contactEmail?: string;
    notificationsEmail?: string;
    phone?: string;
    address?: string;
  };
  guardian?: {
    id: string;
    firstName: string;
    secondName?: string | null;
    email?: string;
    phone?: string;
  };
}

export interface PortalSummary {
  totalBookings: number;
  totalClasses: number;
  completedClasses: number;
  upcomingClasses: number;
  totalCharged: number;
  totalPaid: number;
  balance?: number;
  outstandingBalance: number;
  creditBalance?: number;
  walletBalance: number;
  currency: string;
}

export interface PortalClass {
  id: string;
  bookingId: string;
  activityName: string;
  productTitle: string;
  levelName?: string | null;
  modalityName?: string | null;
  startTime: string;
  endTime: string;
  actualStartTime?: string | null;
  actualEndTime?: string | null;
  status: number;
  statusLabel: string;
  attendance?: number | null;
  attendanceLabel: string;
  instructorName: string;
  notes?: string | null;
}

export interface PortalBooking {
  id: string;
  status: number;
  statusLabel: string;
  hours?: number | null;
  arrivalDate?: string | null;
  departureDate?: string | null;
  preferredStartTime?: string | null;
  snapshotPrice?: number | string | null;
  currency: string;
  createdAt: string;
  activity?: { id: string; name: string } | null;
  product?: { id: string; title: string; publicDescription?: string | null } | null;
  modality?: { id: string; modalityName: string } | null;
  level?: { id: string; levelName: string } | null;
  lessonsCount: number;
  completedLessonsCount: number;
}

export interface PortalTransaction {
  id: string;
  type: number;
  typeName: string;
  direction: number;
  amount: number;
  currency: string;
  forItem: string;
  invoiceIdentifier?: string | null;
  createdAt: string;
}

export interface PortalBoxBooking {
  id: string;
  startDate: string;
  endDate: string;
  arrivedAt?: string | null;
  freedAt?: string | null;
  status: number;
  statusLabel: string;
  amount?: number | string | null;
  currency: string;
  boxLabel?: string | null;
  zoneName?: string | null;
  sizeName?: string | null;
  createdAt: string;
}

export interface PortalRental {
  id: string;
  startDate: string;
  endDate: string;
  status: number;
  statusLabel: string;
  productName: string;
  createdAt: string;
}

export interface AvailableSchool {
  studentId: string;
  schoolId: string;
  schoolName: string;
  schoolLogo?: string | null;
  currency: string;
}

export interface GenerateTokenResponse {
  token: string;
  link: string;
  record: {
    id: string;
    studentId: string;
    expiresAt: string;
    createdAt: string;
  };
}

export interface VerifyTokenResponse {
  tokenRecord: {
    id: string;
    studentId: string;
    expiresAt: string;
  };
  student: PortalStudent;
  school?: PortalStudent['school'];
  availableSchools?: AvailableSchool[];
  summary: PortalSummary;
  breakdown?: any;
  bookings: PortalBooking[];
  classes: PortalClass[];
  transactions: PortalTransaction[];
  boxBookings: PortalBoxBooking[];
  rentals: PortalRental[];
}

@Injectable({ providedIn: 'root' })
export class PortalAccessTokenService {
  private http = inject(HttpClient);

  /**
   * Generate a portal access token for a student by their studentId.
   * Called by school admin from the student profile panel.
   */
  generateToken(studentId: string, expiresInMinutes = 10): Observable<GenerateTokenResponse> {
    return this.http.post<GenerateTokenResponse>(
      `${apis.portalAccessTokens}/${studentId}`,
      { expiresInMinutes }
    );
  }

  /**
   * Verify a raw 256-bit token (called from the portal-access page on load).
   * Supports optional schoolId to view a specific school's details.
   * This endpoint is public — no Authorization header needed.
   */
  verifyToken(rawToken: string, schoolId?: string): Observable<VerifyTokenResponse> {
    let params = new HttpParams();
    if (schoolId) {
      params = params.set('schoolId', schoolId);
    }
    return this.http.get<VerifyTokenResponse>(
      `${apis.portalAccessTokens}/verify/${rawToken}`,
      { params }
    );
  }

  /**
   * Send a portal access link to a student by their email address.
   * The backend looks up the student and sends the email if found.
   * Always returns a success-like message (no user-existence leak).
   */
  sendByEmail(email: string, schoolId?: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${apis.portalAccessTokens}/send-by-email`,
      { email, schoolId }
    );
  }

  /**
   * Revoke a portal access token by its token ID.
   */
  revokeToken(tokenId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(
      `${apis.portalAccessTokens}/${tokenId}/revoke`
    );
  }
}
